import { useEffect, useRef, useState } from "react";
import Modal from "app/components/Modals/SimpleModal";
import Button from "app/storybookComponents/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { INTEGRATION_LOOKUP } from "../../containers/AdminConsole/constants";
import { toast } from "react-toastify";
import { useAppDispatch } from "utils/redux/hooks";

// --- Generic thunks ---
import {
  connectIntegration,
  fetchIntegrationStatus,
} from "app/containers/AdminConsole/slice";

const POPUP_FEATURES =
  "width=650,height=650,noopener,noreferrer,scrollbars=yes,resizable=yes";

interface Props {
  show: boolean;
  hideModal: () => void;
  platform: string;
  onRefreshIntegrationStatus: () => void;
}

export default function IntegrationSetupModal({
  show,
  hideModal,
  platform,
  onRefreshIntegrationStatus,
}: Readonly<Props>) {
  const dispatch = useAppDispatch();
  const details = INTEGRATION_LOOKUP[platform];

  const [step, setStep] = useState(1);
  const [waiting, setWaiting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const popupRef = useRef<Window | null>(null);
  const pollTimer = useRef<number | null>(null);
  const pollStartedAt = useRef<number | null>(null);

  const nextStep = () => setStep((s) => Math.min(s + 1, 2));
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const startPolling = (provider: string) => {
    if (pollTimer.current) return;

    pollStartedAt.current = Date.now();

    pollTimer.current = window.setInterval(async () => {
      const elapsed = Date.now() - (pollStartedAt.current || Date.now());

      if (elapsed > 120000) {
        stopPolling();
        setWaiting(false);
        setErrorMsg("Timeout waiting for admin consent. Please try again.");
        return;
      }

      try {
        const result = await dispatch(
          fetchIntegrationStatus(provider)
        ).unwrap();
        if (result.hasIntegration) {
          stopPolling();
          onAuthSuccess();
        }
      } catch {
        // Ignore transient polling failures; retry on the next interval.
      }
    }, 2000);
  };

  const stopPolling = () => {
    if (pollTimer.current) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (!event.data || typeof event.data !== "object") return;

      if (event.data.source !== "integrationOauth") return;

      if (event.data.provider !== platform) return;

      if (event.data.type === "success") {
        onAuthSuccess();
      } else if (event.data.type === "error") {
        onAuthError(event.data.error || "Authorization failed.");
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  const onAuthSuccess = () => {
    stopPolling();
    setWaiting(false);
    setConnected(true);
    setStep(2);
    toast.success(`${details.name} connected successfully.`);
    try {
      popupRef.current?.close();
    } catch {}
  };

  const onAuthError = (msg: string) => {
    setWaiting(false);
    setConnected(false);
    setErrorMsg(msg);
    toast.error(msg);
    try {
      popupRef.current?.close();
    } catch {}
    stopPolling();
  };

  const handleAuthorize = async () => {
    setErrorMsg(null);

    let url: string;
    try {
      url = await dispatch(connectIntegration(platform)).unwrap();
    } catch {
      setErrorMsg(`Could not start ${details.name} authorization.`);
      return;
    }

    popupRef.current = window.open(url, `${platform}AuthPopup`, POPUP_FEATURES);

    setWaiting(true);
    startPolling(platform);

    // Detect popup close
    const popupWatcher = window.setInterval(() => {
      if (popupRef?.current?.closed) {
        window.clearInterval(popupWatcher);
      }
    }, 600);
  };

  const handleCloseModal = () => {
    stopPolling();
    setWaiting(false);
    hideModal();
    setTimeout(() => {
      setStep(1);
      setConnected(false);
      setErrorMsg(null);
    }, 300);
  };

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      className="integration-setup-modal"
      size="lg"
      title={`Setup ${details.name} Integration`}
    >
      {/* STEP 1 */}
      {step === 1 && (
        <div className="text-start">
          <h4>How Coach Bo will be deployed</h4>
          <p className="text-gray-600">
            Coach Bo will be rolled out organization-wide.
          </p>

          <div className="border rounded p-3 mt-3 bg-light">
            <label className="d-block mb-2">
              <strong>Organization-wide</strong>
            </label>
            <ul className="mb-1">
              <li>Enable for all employees by default</li>
              <li>Users can opt out in their personal settings</li>
              <li>Faster rollout, higher adoption rates</li>
            </ul>
            <small className="text-success">
              ~ 85 %+ completion rate · low setup complexity
            </small>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="text-center mt-2.5">
          <h4 className="font-semibold">
            {connected
              ? `Connected to ${details.name}`
              : `Connect to ${details.name}`}
          </h4>
          <p className="mt-1.5 text-gray-600">
            {connected
              ? "Your organization has granted the required permissions."
              : `You will be redirected to ${details.name} to authorize the connection.`}
          </p>

          <div className="border border-gray-200 rounded-xl py-8 px-6 mt-5 inline-block bg-white w-full max-w-[420px]">
            <div
              className="w-[60px] h-[60px] rounded-lg mx-auto mb-4 flex justify-center items-center"
              style={{ backgroundColor: details.color }}
            >
              <FontAwesomeIcon
                icon={details.icon}
                className="text-white text-[28px]"
              />
            </div>

            <h5 className="font-semibold">
              {connected
                ? `Authorized in ${details.name}`
                : `Authorize ${details.name}`}
            </h5>

            {!connected && (
              <p className="text-sm text-gray-500 mt-2 mb-5">
                Coach Bo needs permissions to send check-ins and receive
                responses.
              </p>
            )}

            {connected ? (
              <div className="badge bg-success mt-4 py-2.5 px-3">Connected</div>
            ) : (
              <Button
                onClick={handleAuthorize}
                variant="secondary-blue"
                className="w-full"
                disabled={waiting}
              >
                {waiting
                  ? "Waiting for admin consent..."
                  : `Authorize in ${details.name}`}
              </Button>
            )}

            {waiting && (
              <div className="mt-3 text-xs text-gray-500">
                Keep this tab open while you complete the {details.name}{" "}
                consent.
              </div>
            )}

            {errorMsg && (
              <div className="alert alert-danger mt-3 text-left" role="alert">
                {errorMsg}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div className="mt-6 flex justify-between">
        <Button
          onClick={prevStep}
          variant="secondary-blue"
          disabled={step === 1 || waiting}
        >
          Back
        </Button>

        {step < 2 ? (
          <Button onClick={nextStep} disabled={waiting}>
            Next
          </Button>
        ) : (
          <Button
            onClick={() => {
              handleCloseModal();
              onRefreshIntegrationStatus();
            }}
            disabled={!connected || waiting}
          >
            Finish
          </Button>
        )}
      </div>
    </Modal>
  );
}
